/**
 * Configurações (docs/spec/settings.md): três grupos de seções, Conta (Perfil, Conta e segurança,
 * Plano), Preferências (Geral, Chat, Dados, Notificações, Áudio e vídeo) e Suporte (Solução de
 * problemas), com a busca. No computador, a barra lateral e a seção ao lado; numa tela estreita
 * (celular, ou janela abaixo de 720 px), a lista das seções e, ao tocar, a seção inteira com o voltar.
 *
 * O que cada seção tem está em settingsModel.ts. As preferências gravam na hora, neste computador; o
 * perfil, a conta e o plano são do servidor.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LANGUAGES, useLanguage, useT } from '../i18n/index.js';
import { Avatar } from '../social/Avatar.js';
import { useApp } from '../store.js';
import { Icon } from '../ui/icons.js';
import { useMobile } from '../ui/mobile.js';
import type { OpenSection } from './SettingRow.js';
import { SettingsSection } from './SettingsSection.js';
import { GROUPS, SECTIONS, resolveSection, searchSettings, sectionSummary, type SectionId } from './settingsModel.js';
import './config.css';
import './settings.css';

/** abaixo desta largura, a lista e o detalhe no lugar da barra lateral */
const NARROW_QUERY = '(max-width: 720px)';

function useNarrow(): boolean {
  const mobile = useMobile();
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(NARROW_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia?.(NARROW_QUERY);
    if (!query) return;
    const change = () => setNarrow(query.matches);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  return mobile || narrow;
}

/** cada pedido de destaque é outro, mesmo para a mesma linha */
let highlightSeq = 0;
const highlightOf = (row: string | null | undefined) => (row ? { row, nonce: (highlightSeq += 1) } : null);

export function ConfigurationState({ initialPage, initialRow }: { initialPage?: string | undefined; initialRow?: string | undefined }) {
  const t = useT();
  const narrow = useNarrow();
  const [section, setSection] = useState<SectionId | null>(() => resolveSection(initialPage));
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState<{ row: string; nonce: number } | null>(() => highlightOf(initialRow));
  const [link, setLink] = useState({ page: initialPage, row: initialRow });
  const main = useRef<HTMLDivElement>(null);

  const open: OpenSection = useCallback((id, row) => {
    setSection(id);
    setQuery('');
    setHighlight(highlightOf(row));
  }, []);

  // um link com a tela já aberta (Arquivos → Plano) troca de seção e destaca a linha pedida
  if (link.page !== initialPage || link.row !== initialRow) {
    setLink({ page: initialPage, row: initialRow });
    const id = resolveSection(initialPage);
    if (id) open(id, initialRow);
  }

  // cada seção começa do topo (a busca rola depois até a linha)
  useEffect(() => {
    main.current?.scrollTo?.({ top: 0 });
  }, [section]);

  const searching = query.trim() !== '';
  const search = <SearchBox value={query} onChange={setQuery} />;
  const results = searching ? <SearchResults query={query} onOpen={open} /> : null;

  if (narrow && section === null) {
    return (
      <div className="rd-prefs rd-prefs--narrow">
        <div className="rd-prefs__list">
          <h2 className="rd-prefs__listTitle">{t('logged.nav.settings')}</h2>
          {search}
          {results ?? (
            <>
              <MeCard big onOpen={() => open('profile')} />
              <SectionList onOpen={open} />
            </>
          )}
        </div>
      </div>
    );
  }

  const current = section ?? 'general';
  const page = (
    <div className="rd-prefs__main" ref={main}>
      <div className="rd-prefs__page">
        <SettingsSection key={current} id={current} highlight={highlight} onOpen={open} />
      </div>
    </div>
  );

  if (narrow) {
    return (
      <div className="rd-prefs rd-prefs--narrow">
        <div className="rd-prefs__bar">
          <button type="button" className="rd-prefs__back" aria-label={t('prefs.backTo')} onClick={() => setSection(null)}>
            <Icon name="chevronLeft" size={22} />
            {t('logged.nav.settings')}
          </button>
        </div>
        {page}
      </div>
    );
  }

  return (
    <div className="rd-prefs">
      <aside className="rd-prefs__side" aria-label={t('prefs.sections')}>
        <MeCard onOpen={() => open('profile')} />
        {search}
        {results ?? <SectionNav current={current} onOpen={open} />}
      </aside>
      {page}
    </div>
  );
}

/** O cartão de quem está usando: abre o Perfil. */
function MeCard({ onOpen, big = false }: { onOpen: () => void; big?: boolean }) {
  const t = useT();
  const me = useApp((s) => s.user);
  if (!me) return null;
  return (
    <button type="button" className={`rd-prefs__me${big ? ' rd-prefs__me--big' : ''}`} onClick={onOpen}>
      <Avatar name={me.displayName} color={me.color} size={big ? 52 : 40} userId={me.id} avatarVersion={me.avatarVersion} />
      <span className="rd-prefs__meText">
        <strong>{me.displayName}</strong>
        <span>{t(big ? 'prefs.me.mobile' : 'prefs.me')}</span>
      </span>
      {big ? <Icon name="chevronRight" size={18} /> : null}
    </button>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const t = useT();
  return (
    <label className="rd-searchBox rd-prefs__search">
      <Icon name="search" size={16} />
      <input
        type="search"
        value={value}
        placeholder={t('prefs.search')}
        aria-label={t('prefs.search')}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onChange('');
        }}
      />
    </label>
  );
}

/** Os resultados da busca no lugar da lista de seções: o nome da linha e onde ela fica. */
function SearchResults({ query, onOpen }: { query: string; onOpen: OpenSection }) {
  const t = useT();
  const results = searchSettings(query, t);
  return (
    <div className="rd-prefs__results" role="region" aria-label={t('prefs.search.title')}>
      <div className="rd-prefs__groupLabel" aria-live="polite">
        {results.length ? t('prefs.search.count', { n: results.length }) : t('prefs.search.title')}
      </div>
      {results.length ? (
        <ul>
          {results.map((result) => (
            <li key={`${result.section}:${result.row ?? ''}`}>
              <button type="button" className="rd-prefs__result" onClick={() => onOpen(result.section, result.row)}>
                <span>{result.label}</span>
                <small>{result.path}</small>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rd-prefs__desc">{t('prefs.search.none')}</p>
      )}
    </div>
  );
}

/** A barra lateral: os grupos e as seções de cada um. */
function SectionNav({ current, onOpen }: { current: SectionId; onOpen: OpenSection }) {
  const t = useT();
  return (
    <nav className="rd-prefs__nav" aria-label={t('logged.nav.settings')}>
      {GROUPS.map((group) => (
        <div key={group.id} className="rd-prefs__group">
          <div className="rd-navGroupLabel rd-prefs__groupLabel" id={`pref-group-${group.id}`}>
            {t(group.label)}
          </div>
          <ul aria-labelledby={`pref-group-${group.id}`}>
            {SECTIONS.filter((s) => s.group === group.id).map((s) => (
              <li key={s.id}>
                <button type="button" className={`rd-navItem rd-prefs__navItem${s.id === current ? ' is-on' : ''}`} aria-current={s.id === current ? 'page' : undefined} onClick={() => onOpen(s.id)}>
                  <Icon name={s.icon} size={18} />
                  <span>{t(s.title)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/** A lista da tela estreita: os grupos em blocos, cada seção com o ícone, o resumo e a seta. */
function SectionList({ onOpen }: { onOpen: OpenSection }) {
  const t = useT();
  const settings = useApp((s) => s.settings);
  const language = useLanguage((s) => s.language);
  const languageLabel = LANGUAGES.find((l) => l.id === language)?.label ?? language;
  return (
    <nav className="rd-prefs__nav" aria-label={t('logged.nav.settings')}>
      {GROUPS.map((group) => {
        // o Perfil já está no cartão de cima
        const sections = SECTIONS.filter((s) => s.group === group.id && s.id !== 'profile');
        return (
          <div key={group.id} className="rd-prefs__group">
            <div className="rd-prefs__groupLabel" id={`pref-group-${group.id}`}>
              {t(group.label)}
            </div>
            <ul className="rd-prefs__listBox" aria-labelledby={`pref-group-${group.id}`}>
              {sections.map((s) => {
                const summary = sectionSummary(s.id, settings, t, languageLabel);
                return (
                  <li key={s.id}>
                    <button type="button" className="rd-prefs__listItem" onClick={() => onOpen(s.id)}>
                      <span className="rd-prefs__listIcon">
                        <Icon name={s.icon} size={18} />
                      </span>
                      <span className="rd-prefs__listName">{t(s.title)}</span>
                      {summary ? <span className="rd-prefs__listSummary">{summary}</span> : null}
                      <Icon name="chevronRight" size={16} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}
